import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from '../../utils/axiosConfig';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import QuillResizeModule from 'quill-resize-module';
import TagSidebar from './TagSidebar';
import { replaceTags } from '../../utils/tagReplacer';

// Register style-based attributors instead of class-based for better HTML rendering
// This ensures alignment, fonts, and other formatting use inline styles
const AlignStyle = Quill.import('attributors/style/align');
const FontStyle = Quill.import('attributors/style/font');
const SizeStyle = Quill.import('attributors/style/size');
const Parchment = Quill.import('parchment');

// Configure custom fonts - 15 popular Word-compatible fonts
// Font names must match exactly (case-sensitive in Quill, but backend will handle mapping)
FontStyle.whitelist = [
  'Arial',
  'Times New Roman',
  'Calibri',
  'Georgia',
  'Verdana',
  'Courier New',
  'Tahoma',
  'Trebuchet MS',
  'Comic Sans MS',
  'Impact',
  'Consolas',
  'Lucida Console',
  'Palatino',
  'Garamond',
  'Book Antiqua'
];

// Configure custom font sizes - common sizes used in documents
SizeStyle.whitelist = ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

// Configure line height (line spacing) - common values used in documents
// Scope BLOCK so it applies to entire paragraphs, not just selected text
const LineHeightStyle = new Parchment.Attributor.Style('lineHeight', 'line-height', {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['1.0', '1.15', '1.5', '2.0', '2.5']
});

Quill.register(AlignStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);
Quill.register(LineHeightStyle, true);

// Enable resize module for images/media (Quill 2 compatible)
Quill.register('modules/resize', QuillResizeModule);

const EmailTemplateTab = () => {
  const { t } = useTranslation(['forms', 'buttons', 'messages', 'pages']);
  const { user } = useSelector((state) => state.userLoaded);
  const quillRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [activeField, setActiveField] = useState('content'); // Track which field is active
  const [subjectSelection, setSubjectSelection] = useState({ start: 0, end: 0 }); // Track cursor position in subject

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    tags: [],
  });

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }], // All heading levels (h1-h6) + normal text
      [{ 'font': FontStyle.whitelist }], // Custom font list
      [{ 'size': SizeStyle.whitelist }], // Custom font sizes
      [{ 'lineHeight': ['1.0', '1.15', '1.5', '2.0', '2.5'] }], // Line spacing options
      [{ 'color': [] }, { 'background': [] }], // Text color and background color (default palette)
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image', 'code-block'],
    ],
    resize: {
      modules: ['Resize', 'DisplaySize'],
    },
  };

  // Fetch templates
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Fix dropdown positioning to escape toolbar clipping
  useEffect(() => {
    const fixDropdownPosition = () => {
      // Find the ReactQuill toolbar in this component's scope
      const wrapper = document.querySelector('[data-quill-component="email-template"]');
      const toolbar = wrapper 
        ? wrapper.querySelector('.ql-toolbar.ql-snow')
        : document.querySelector('.ql-toolbar.ql-snow');
      
      if (!toolbar) return;

      const expandedPickers = toolbar.querySelectorAll('.ql-picker.ql-expanded');
      expandedPickers.forEach((picker) => {
        const options = picker.querySelector('.ql-picker-options');
        if (!options) return;

        // Get button position (the clickable label)
        const pickerLabel = picker.querySelector('.ql-picker-label');
        if (!pickerLabel) return;

        const rect = pickerLabel.getBoundingClientRect();
        
        // Position dropdown using fixed positioning to escape toolbar clipping
        // Fixed positioning is relative to viewport, so getBoundingClientRect() values are correct
        options.style.position = 'fixed';
        options.style.top = `${rect.bottom + 2}px`;
        options.style.left = `${rect.left}px`;
        options.style.zIndex = '99999';
        options.style.width = 'auto';
        options.style.minWidth = `${rect.width}px`; // At least as wide as the button
        options.style.maxWidth = '300px'; // But not too wide
        options.style.whiteSpace = 'nowrap'; // Prevent text wrapping
      });
    };

    // Check for dropdown changes with MutationObserver
    const wrapper = document.querySelector('[data-quill-component="email-template"]');
    const toolbar = wrapper 
      ? wrapper.querySelector('.ql-toolbar.ql-snow')
      : document.querySelector('.ql-toolbar.ql-snow');
    
    if (!toolbar) return;

    const observer = new MutationObserver(() => {
      // Use setTimeout to let Quill finish rendering
      setTimeout(fixDropdownPosition, 10);
    });

    observer.observe(toolbar, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Also fix on click events
    const handleClick = () => setTimeout(fixDropdownPosition, 20);
    toolbar.addEventListener('click', handleClick, true);

    // Use requestAnimationFrame for initial fix after render
    requestAnimationFrame(() => {
      setTimeout(fixDropdownPosition, 50);
    });

    return () => {
      observer.disconnect();
      toolbar.removeEventListener('click', handleClick, true);
    };
  }, [formData.content, selectedTemplate]); // Re-run when template changes

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/email-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setSnackbarMessage(t('messages:error.fetching_templates'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject || '',
      content: template.content || '',
      tags: template.tags || [],
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle tag insertion
  const handleTagInsert = (tagName) => {
    const tagText = `{{${tagName}}}`;
    
    if (activeField === 'subject') {
      // Insert into Subject TextField
      const start = subjectSelection.start;
      const end = subjectSelection.end;
      const currentValue = formData.subject || '';
      const newValue = currentValue.slice(0, start) + tagText + currentValue.slice(end);
      
      handleInputChange('subject', newValue);
      
      // Set cursor position after inserted tag
      setTimeout(() => {
        if (subjectInputRef.current) {
          const input = subjectInputRef.current.querySelector('input');
          if (input && input.setSelectionRange) {
            const newPosition = start + tagText.length;
            input.focus();
            input.setSelectionRange(newPosition, newPosition);
            setSubjectSelection({ start: newPosition, end: newPosition });
          }
        }
      }, 10);
    } else if (quillRef.current) {
      // Insert into Content ReactQuill
      const quill = quillRef.current.getEditor();
      quill.focus(); // Ensure Quill has focus
      const range = quill.getSelection(true);
      if (range) {
        quill.insertText(range.index, tagText, 'user');
        quill.setSelection(range.index + tagText.length);
      } else {
        // If no selection, append at the end
        const length = quill.getLength();
        quill.insertText(length - 1, tagText, 'user');
        quill.setSelection(length + tagText.length - 1);
      }
      // Update form data
      handleInputChange('content', quill.root.innerHTML);
    }
  };

  // Handle content change from ReactQuill
  const handleContentChange = (content) => {
    handleInputChange('content', content);
  };

  // Create new template
  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      subject: '',
      content: '',
      tags: [],
    });
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setContents([]);
    }
  };

  // Save template
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setSnackbarMessage(t('messages:error.template_name_required'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      let response;
      if (selectedTemplate) {
        // Update existing template
        response = await axios.put(`/api/email-templates/${selectedTemplate._id}`, formData);
        setSnackbarMessage(t('messages:template_updated'));
      } else {
        // Create new template
        response = await axios.post('/api/email-templates', formData);
        setSnackbarMessage(t('messages:template_created'));
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Update templates list
      await fetchTemplates();
      
      // Use the response data to update the selected template and formData
      // This ensures we have the latest data from the server (including post-processed content)
      if (response && response.data) {
        const updatedTemplate = response.data;
        setSelectedTemplate(updatedTemplate);
        setFormData({
          name: updatedTemplate.name,
          subject: updatedTemplate.subject || '',
          content: updatedTemplate.content || '',
          tags: updatedTemplate.tags || [],
        });
        
        // React Quill will automatically update via the value prop (formData.content)
        // No need to manually set innerHTML as it bypasses Quill's Delta and causes formatting issues
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setSnackbarMessage(
        error.response?.data?.message || t('messages:error.saving_template')
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Delete template
  const handleDelete = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(t('messages:confirm.delete_template'))) return;

    try {
      setLoading(true);
      await axios.delete(`/api/email-templates/${selectedTemplate._id}`);
      setSnackbarMessage(t('messages:template_deleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setSelectedTemplate(null);
      setFormData({
        name: '',
        subject: '',
        content: '',
        tags: [],
      });
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      setSnackbarMessage(
        error.response?.data?.message || t('messages:error.deleting_template')
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };


  // Preview template
  const handlePreview = () => {
    // Create mock context for preview
    const mockContext = {
      recipient: { firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com' },
      selectedCase: { name: 'Sample Case', fileNo: 'CASE-001' },
      from: { firstName: user?.firstName || 'User', lastName: user?.lastName || 'Name' },
      user: user,
      client: { firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com', businessName: 'Sample Client Ltd' },
    };
    const preview = replaceTags(formData.content, mockContext);
    setPreviewContent(preview);
    setPreviewOpen(true);
  };

  const isManager = user?.role === 'Manager';

  // Modern text field styles matching the new design
  const modernTextFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F9FAFB',
      borderRadius: '8px',
      fontSize: '13px',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E5E7EB',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#D1D5DB',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3B82F6',
        borderWidth: '1.5px',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '13px',
      color: '#6B7280',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#3B82F6',
    },
    '& .MuiInputBase-input': {
      fontSize: '13px',
      color: '#374151',
    },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: "flex", gap: 0 }}>
        {/* Tags Sidebar - Left Side */}
        <TagSidebar onTagClick={handleTagInsert} />

        {/* Template Editor - Right Side */}
        <Box sx={{ flex: 1, backgroundColor: '#fff' }}>
          <Box sx={{ p: 3 }}>
            {/* Header Section */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: '1px solid #E5E7EB',
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '15px', 
                  fontWeight: 600,
                  color: '#1F2937',
                  letterSpacing: '-0.01em',
                }}
              >
                {selectedTemplate
                  ? t('forms:templates.edit_template')
                  : t('forms:templates.create_email_template')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {isManager && (
                  <Tooltip title={t('forms:templates.create_email_template')}>
                    <IconButton
                      size="small"
                      onClick={handleCreate}
                      sx={{
                        color: '#3B82F6',
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          backgroundColor: '#EFF6FF',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {selectedTemplate && isManager && (
                  <>
                    <Tooltip title={t('buttons:preview_template')}>
                      <IconButton 
                        size="small" 
                        onClick={handlePreview}
                        sx={{
                          color: '#6B7280',
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('buttons:delete_template')}>
                      <IconButton 
                        size="small" 
                        onClick={handleDelete}
                        sx={{
                          color: '#EF4444',
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: '#FEF2F2',
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>

            <Grid container spacing={2.5}>
              {/* Template Selection Dropdown and Template Name in same row */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={templates}
                  value={selectedTemplate}
                  onInputChange={(event, newInputValue, reason) => {
                    // Handle clear action
                    if (reason === 'clear') {
                      handleCreate();
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSelectTemplate(newValue);
                    } else {
                      handleCreate();
                    }
                  }}
                  getOptionLabel={(option) => option.name || ''}
                  renderOption={(props, option) => (
                    <Box 
                      component="li" 
                      {...props}
                      sx={{
                        fontSize: '13px',
                        py: 1.25,
                        px: 2,
                        borderRadius: '6px',
                        mx: 0.5,
                        my: 0.25,
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: '#EFF6FF',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: '#EFF6FF',
                          fontWeight: 500,
                        },
                      }}
                    >
                      {option.name}
                      {option.isDefault && (
                        <Typography variant="caption" sx={{ ml: 1, color: '#9CA3AF', fontSize: '11px' }}>
                          ({t('forms:templates.default')})
                        </Typography>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forms:labels.template')}
                      placeholder={!selectedTemplate ? 'New' : ''}
                      size="small"
                      sx={modernTextFieldStyles}
                    />
                  )}
                  noOptionsText="New"
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1.5,
                        borderRadius: '10px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #E5E7EB',
                        overflow: 'hidden',
                        '& .MuiAutocomplete-listbox': {
                          py: 0.75,
                          maxHeight: 280,
                        },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#9CA3AF',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('forms:template.name')}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!isManager && selectedTemplate?.isDefault}
                  size="small"
                  sx={modernTextFieldStyles}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('forms:template.subject')}
                  value={formData.subject}
                  onChange={(e) => {
                    handleInputChange('subject', e.target.value);
                    // Track cursor position
                    const input = e.target;
                    setSubjectSelection({
                      start: input.selectionStart || formData.subject.length,
                      end: input.selectionEnd || formData.subject.length,
                    });
                  }}
                  onFocus={(e) => {
                    setActiveField('subject');
                    const input = e.target;
                    setSubjectSelection({
                      start: input.selectionStart || 0,
                      end: input.selectionEnd || 0,
                    });
                  }}
                  onSelect={(e) => {
                    const input = e.target;
                    setSubjectSelection({
                      start: input.selectionStart || 0,
                      end: input.selectionEnd || 0,
                    });
                  }}
                  disabled={!isManager && selectedTemplate?.isDefault}
                  size="small"
                  sx={modernTextFieldStyles}
                  placeholder={t('forms:template.subject_placeholder')}
                  inputRef={subjectInputRef}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1.5,
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  {t('forms:template.content')}
                </Typography>
                <Box 
                  sx={{ 
                    position: 'relative', 
                    overflow: 'visible', 
                    zIndex: 1,
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    '& .ql-toolbar.ql-snow': {
                      borderRadius: '8px 8px 0 0',
                      borderColor: '#E5E7EB',
                      backgroundColor: '#F9FAFB',
                      borderBottom: '1px solid #E5E7EB',
                    },
                    '& .ql-container.ql-snow': {
                      borderRadius: '0 0 8px 8px',
                      borderColor: '#E5E7EB',
                      borderTop: 'none',
                      fontSize: '14px',
                    },
                    '& .ql-editor': {
                      minHeight: '280px',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#374151',
                    },
                    '& .ql-editor.ql-blank::before': {
                      color: '#9CA3AF',
                      fontStyle: 'normal',
                    },
                  }}
                  data-quill-component="email-template"
                  onFocus={() => setActiveField('content')}
                >
                  {/* Custom CSS for font and size dropdown labels */}
                  <style>
                    {`
                      /* Modern Toolbar styling */
                      .ql-toolbar.ql-snow {
                        display: flex;
                        flex-wrap: wrap;
                        align-items: center;
                        gap: 4px 8px;
                        padding: 8px 12px !important;
                        position: relative;
                        overflow: visible;
                      }
                      
                      /* Toolbar button styling */
                      .ql-toolbar.ql-snow button {
                        width: 28px !important;
                        height: 28px !important;
                        padding: 4px !important;
                        border-radius: 4px !important;
                        transition: all 0.15s ease !important;
                      }
                      .ql-toolbar.ql-snow button:hover {
                        background-color: #E5E7EB !important;
                      }
                      .ql-toolbar.ql-snow button.ql-active {
                        background-color: #DBEAFE !important;
                        color: #3B82F6 !important;
                      }
                      .ql-toolbar.ql-snow button.ql-active .ql-stroke {
                        stroke: #3B82F6 !important;
                      }
                      .ql-toolbar.ql-snow button.ql-active .ql-fill {
                        fill: #3B82F6 !important;
                      }
                      
                      /* Toolbar groups spacing */
                      .ql-toolbar.ql-snow .ql-formats {
                        display: inline-flex;
                        align-items: center;
                        margin-right: 0;
                      }

                      /* Header picker styling */
                      .ql-snow .ql-picker.ql-header {
                        width: auto !important;
                        min-width: 52px;
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label {
                        width: max-content;
                        padding-right: 18px;
                        border-radius: 4px;
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item::before {
                        content: 'P';
                      }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before { content: 'H1'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before { content: 'H2'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before { content: 'H3'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before { content: 'H4'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before { content: 'H5'; }
                      .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
                      .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before { content: 'H6'; }

                      /* Font picker styling */
                      .ql-snow .ql-picker.ql-font {
                        width: auto !important;
                        min-width: 50px;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label {
                        width: max-content;
                        max-width: 200px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        padding-right: 18px;
                        border-radius: 4px;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label::before {
                        display: inline-block;
                        max-width: 100%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        vertical-align: bottom;
                      }

                      /* Picker hover and focus states */
                      .ql-snow .ql-picker .ql-picker-label:hover {
                        background-color: #E5E7EB;
                        border-radius: 4px;
                      }

                      /* Editor container */
                      .ql-container.ql-snow {
                        position: relative;
                        z-index: 1;
                      }

                      /* Dropdown options styling */
                      .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
                        position: fixed !important;
                        z-index: 99999 !important;
                        background-color: white !important;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
                        border: 1px solid #E5E7EB !important;
                        border-radius: 8px !important;
                        max-height: 200px;
                        overflow-y: auto;
                        display: block !important;
                        width: auto !important;
                        white-space: nowrap !important;
                        padding: 4px !important;
                      }
                      .ql-toolbar.ql-snow .ql-picker-options .ql-picker-item {
                        padding: 6px 10px !important;
                        border-radius: 4px !important;
                      }
                      .ql-toolbar.ql-snow .ql-picker-options .ql-picker-item:hover {
                        background-color: #F3F4F6 !important;
                      }

                      /* Font dropdown labels */
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Arial"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial"]::before {
                        content: 'Arial';
                        font-family: 'Arial', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Times New Roman"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman"]::before {
                        content: 'Times New Roman';
                        font-family: 'Times New Roman', serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Calibri"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Calibri"]::before {
                        content: 'Calibri';
                        font-family: 'Calibri', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Georgia"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Georgia"]::before {
                        content: 'Georgia';
                        font-family: 'Georgia', serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Verdana"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana"]::before {
                        content: 'Verdana';
                        font-family: 'Verdana', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Courier New"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Courier New"]::before {
                        content: 'Courier New';
                        font-family: 'Courier New', monospace;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Tahoma"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Tahoma"]::before {
                        content: 'Tahoma';
                        font-family: 'Tahoma', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Trebuchet MS"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Trebuchet MS"]::before {
                        content: 'Trebuchet MS';
                        font-family: 'Trebuchet MS', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Comic Sans MS"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Comic Sans MS"]::before {
                        content: 'Comic Sans MS';
                        font-family: 'Comic Sans MS', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Impact"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Impact"]::before {
                        content: 'Impact';
                        font-family: 'Impact', sans-serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Consolas"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Consolas"]::before {
                        content: 'Consolas';
                        font-family: 'Consolas', monospace;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Lucida Console"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Lucida Console"]::before {
                        content: 'Lucida Console';
                        font-family: 'Lucida Console', monospace;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Palatino"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Palatino"]::before {
                        content: 'Palatino';
                        font-family: 'Palatino', serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Garamond"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Garamond"]::before {
                        content: 'Garamond';
                        font-family: 'Garamond', serif;
                      }
                      .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="Book Antiqua"]::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Book Antiqua"]::before {
                        content: 'Book Antiqua';
                        font-family: 'Book Antiqua', serif;
                      }
                      
                      /* Font-size picker */
                      .ql-snow .ql-picker.ql-size {
                        width: 60px;
                      }
                      .ql-snow .ql-picker.ql-size .ql-picker-label {
                        width: 100%;
                        text-align: left;
                        border-radius: 4px;
                      }
                      
                      /* Size dropdown labels */
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before { content: '11px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: '28px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: '36px'; }
                      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
                      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before { content: '48px'; }
                      
                      /* Line height dropdown labels */
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label {
                        padding-right: 18px;
                        min-width: 90px;
                        border-radius: 4px;
                      }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item::before {
                        content: attr(data-value);
                      }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.0"]::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.0"]::before { content: 'Single (1.0)'; }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.15"]::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.15"]::before { content: '1.15'; }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="1.5"]::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.5"]::before { content: '1.5'; }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="2.0"]::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.0"]::before { content: 'Double (2.0)'; }
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value="2.5"]::before,
                      .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.5"]::before { content: '2.5'; }
                    `}
                  </style>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    style={{ height: 'auto' }}
                    readOnly={!isManager && selectedTemplate?.isDefault}
                  />
                </Box>
              </Grid>

              {isManager && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCreate}
                      disabled={loading}
                      size="small"
                      sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '13px',
                        px: 3,
                        py: 0.875,
                        minWidth: '80px',
                        borderColor: '#E5E7EB',
                        color: '#374151',
                        backgroundColor: '#fff',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#D1D5DB',
                          backgroundColor: '#F9FAFB',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {t('buttons:primary.new')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={loading || !formData.name.trim()}
                      size="small"
                      sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '13px',
                        px: 3,
                        py: 0.875,
                        minWidth: '80px',
                        backgroundColor: '#3B82F6',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#2563EB',
                          boxShadow: 'none',
                        },
                        '&:disabled': {
                          backgroundColor: '#E5E7EB',
                          color: '#9CA3AF',
                        },
                      }}
                    >
                      {selectedTemplate ? t('buttons:primary.save') : t('buttons:primary.create')}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '16px', 
          fontWeight: 600, 
          color: '#1F2937',
          borderBottom: '1px solid #E5E7EB',
          py: 2,
        }}>
          {t('forms:templates.preview')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              p: 3,
              minHeight: '300px',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                p: 3,
                minHeight: '250px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151',
              }}
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #E5E7EB', 
          p: 2,
        }}>
          <Button 
            onClick={() => setPreviewOpen(false)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '13px',
              px: 3,
              color: '#374151',
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
            }}
          >
            {t('buttons:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateTab;
